import { Button, Grid2 as Grid, Paper, Typography } from '@mui/material'
import AlertDialog from 'components/AlertDialog'
import NoticeDialog from 'components/NoticeDialog'
import useAuth from 'hooks/useAuth'
import { useState, useCallback, useEffect } from 'react'
import { CircularProgress } from '@mui/material'
import type { Organization } from 'types/organization'
import { useFinchConnect, SuccessEvent, ErrorEvent } from '@tryfinch/react-connect'
import { useFinch, FinchSessionSuccessRequest } from 'hooks/useFinch'
import { CredentialsDTO, ServiceProviderStatus } from 'types/credentials'

interface OrganizationAPIMgmtSectionProps {
  organization: Organization
}

const OrganizationConnectionMgmtSection = ({ organization }: OrganizationAPIMgmtSectionProps) => {
  const { token } = useAuth()
  const {
    getConnectionStatus,
    isLoadingConnectionStatus,
    getSession,
    isLoadingSession,
    getReauthenticateSession,
    isLoadingReauthenticateSession,
    handleSessionSuccess,
    isHandlingSessionSuccess: isSendingSessionSuccess,
    error: errorSendingSessionSuccess,
  } = useFinch(token!)
  const [showSaveErrorDialog, setShowSaveErrorDialog] = useState(false)
  const [showFinchConnectErrorDialog, setShowFinchConnectErrorDialog] = useState(false)
  const [code, setCode] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [reauthenticateSessionId, setReauthenticateSessionId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [connectionStatus, setConnectionStatus] = useState<CredentialsDTO | null>(null)
  const [connectionStatusError, setConnectionStatusError] = useState<string | null>(null)
  const [showConnectionStatusErrorDialog, setShowConnectionStatusErrorDialog] = useState(false)

  // Only show for root organizations
  const isRootOrg = organization.id === organization.rootOrganizationId

  const handleCloseErrorDialog = () => {
    setShowSaveErrorDialog(false)
    setShowFinchConnectErrorDialog(false)
    setShowConnectionStatusErrorDialog(false)
  }

  // Define callbacks
  /**
   * @param {SuccessEvent} e - The success event containing authorization code and optional state
   */
  const onSuccess = (e: SuccessEvent) => {
    console.log('Success event received from Finch Connect')
    setCode(e.code)
    let body: FinchSessionSuccessRequest = {
      organizationId: organization.id,
      organizationName: organization.name,
      code: e.code,
    }
    handleSessionSuccess(body)
  }

  /**
   * @param {ErrorEvent} e - The error event containing error message and optional error type
   */
  const onError = (e: ErrorEvent) => {
    console.error(e.errorMessage, e.errorType)
    console.log('Testing if this is being called')
  }

  const onClose = () => console.log('User exited Finch Connect')

  // Initialize the FinchConnect hook
  const { open } = useFinchConnect({
    onSuccess,
    onError,
    onClose,
  })

  useEffect(() => {
    if (errorSendingSessionSuccess) {
      setShowSaveErrorDialog(true)
    }
  }, [errorSendingSessionSuccess])

  useEffect(() => {
    if (connectionStatusError) {
      setShowConnectionStatusErrorDialog(true)
    }
  }, [connectionStatusError])

  useEffect(() => {
    if (organization.rootOrganizationId && isRootOrg) {
      const params: Array<[string, string]> = [['rootOrganizationId', organization.rootOrganizationId]]
      getConnectionStatus(params)
        .then((response) => setConnectionStatus(response))
        .catch((error) => {
          console.error('Failed to get connection status:', error)
          setConnectionStatusError(error.message || 'Failed to get connection status')
        })
    }
  }, [organization.rootOrganizationId, isRootOrg, getConnectionStatus])

  // Generate a session ID using the /connect/sessions endpoint on the Finch API
  // See the docs here https://developer.tryfinch.com/api-reference/connect/new-session#create-a-new-connect-session
  const createSession = useCallback(async () => {
    try {
      setError(null)
      setIsLoading(true)
      setReauthenticateSessionId(null)
      const response = await getSession({ rootOrganizationId: organization.id })
      setSessionId(response.sessionId)
      return response.sessionId
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create session'
      console.error('Failed to create session:', error)
      setError(errorMessage)
      return null
    } finally {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization.id, organization.name])

  const createReauthenticateSession = useCallback(async () => {
    try {
      setError(null)
      setIsLoading(true)
      setSessionId(null)
      const response = await getReauthenticateSession({ rootOrganizationId: organization.id })
      setReauthenticateSessionId(response.sessionId)
      return response.sessionId
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create session'
      console.error('Failed to create session:', error)
      setError(errorMessage)
      return null
    } finally {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization.id, organization.name])

  const handleConnectFinch = useCallback(async () => {
    let currentSessionId = sessionId
    if (!currentSessionId) {
      currentSessionId = await createSession()
      console.log('Session ID:', currentSessionId)
    }
    if (currentSessionId) {
      open({ sessionId: currentSessionId })
    }
  }, [sessionId, createSession, open])

  const handleReauthenticateFinch = useCallback(async () => {
    let currentSessionId = reauthenticateSessionId
    if (!currentSessionId) {
      currentSessionId = await createReauthenticateSession()
      console.log('Session ID:', currentSessionId)
    }
    if (currentSessionId) {
      open({ sessionId: currentSessionId })
    }
  }, [reauthenticateSessionId, createReauthenticateSession, open])

  useEffect(() => {
    console.log(isLoadingSession)
  }, [isLoadingSession])

  useEffect(() => {
    console.log(isLoadingReauthenticateSession)
  }, [isLoadingReauthenticateSession])

  if (!isRootOrg) return null

  if (isLoadingConnectionStatus) return null

  return (
    <>
      <Paper elevation={0} sx={{ mb: 4, borderRadius: '15px', padding: '15px' }}>
        <Typography variant="h5" gutterBottom mb={2}>
          External Service Connections [{code}]
        </Typography>
        <Grid container spacing={3} p={2}>
          {connectionStatus?.status === ServiceProviderStatus.ServiceProviderStatusNone && (
            <Grid size={{ xs: 12, sm: 4 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleConnectFinch}
                fullWidth
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : null}
              >
                {isLoading ? 'Connecting...' : 'Connect Finch'}
              </Button>
            </Grid>
          )}
          {connectionStatus?.status !== ServiceProviderStatus.ServiceProviderStatusNone && (
            <Grid size={{ xs: 12, sm: 4 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleReauthenticateFinch}
                fullWidth
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : null}
              >
                {isLoading ? 'Connecting...' : 'Reconnect Finch'}
              </Button>
            </Grid>
          )}
          {error && (
            <Grid size={{ xs: 12 }}>
              <Typography color="error" variant="body2">
                Error: {error}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      <AlertDialog
        show={showSaveErrorDialog || showFinchConnectErrorDialog}
        onClose={handleCloseErrorDialog}
        title="Error"
        message={showSaveErrorDialog ? 'Error saving Finch credentials' : 'Error connecting to Finch'}
        okText="OK"
        okColor="error"
      />

      <AlertDialog
        show={showConnectionStatusErrorDialog}
        onClose={handleCloseErrorDialog}
        title="Error"
        message={'Error retrieving Finch connection status'}
        okText="OK"
        okColor="error"
      />

      <NoticeDialog
        show={isSendingSessionSuccess}
        title={'Saving Credentials...'}
        message={'Please wait while we securely save your Finch credentials'}
        showSpinner
      />
    </>
  )
}

export default OrganizationConnectionMgmtSection
